import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getCatalog, getManifest, togglePackStatus, deleteFiles, deletePack } from '../api';
import type { Catalog, CatalogPack, PackManifest } from '../types';
import { ArrowLeft, RefreshCw, Eye, Package, Trash2, Upload, ChevronDown, ChevronRight } from 'react-feather';

export const Packs: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPack, setSelectedPack] = useState<CatalogPack | null>(null);
  const [manifest, setManifest] = useState<PackManifest | null>(null);
  const [loadingManifest, setLoadingManifest] = useState(false);
  const [selectedPacks, setSelectedPacks] = useState<Set<string>>(new Set());
  const [selectedTemplates, setSelectedTemplates] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadCatalog();
  }, []);

  // Auto-load pack if ID is in URL
  useEffect(() => {
    const loadPackFromUrl = async () => {
      if (id && catalog && !selectedPack) {
        const pack = catalog.packs.find(p => p.id === id);
        if (pack) {
          setSelectedPack(pack);
          // Load all media from manifest_all.json
          await loadManifest(pack.id);
        }
      }
    };

    loadPackFromUrl();
  }, [id, catalog, selectedPack, navigate]);

  const loadManifest = async (packId: string) => {
    setLoadingManifest(true);
    try {
      // Load manifest_all.json via API (always use version 1)
      const manifestData = await getManifest(packId, 1, true);
      setManifest(manifestData);
    } catch (err) {
      console.error('Failed to load manifest:', err);
      alert(`Failed to load media: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoadingManifest(false);
    }
  };

  const loadCatalog = async (bustCache: boolean = false) => {
    try {
      setLoading(true);
      const data = await getCatalog(bustCache);
      setCatalog(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  };


  const handleBackToList = () => {
    setSelectedPack(null);
    setManifest(null);
    setSelectedTemplates(new Set());
    navigate('/categories');
  };

  const handleSelectTemplate = (templateId: string) => {
    setSelectedTemplates(prev => {
      const newSet = new Set(prev);
      if (newSet.has(templateId)) {
        newSet.delete(templateId);
      } else {
        newSet.add(templateId);
      }
      return newSet;
    });
  };

  const handleSelectAllTemplates = () => {
    if (!manifest) return;
    if (selectedTemplates.size === manifest.items.length) {
      setSelectedTemplates(new Set());
    } else {
      setSelectedTemplates(new Set(manifest.items.map(t => t.id)));
    }
  };

  const handleBulkDeleteTemplates = async () => {
    if (!selectedPack || !manifest || selectedTemplates.size === 0) return;

    if (!confirm(`Delete ${selectedTemplates.size} selected template(s)? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoadingManifest(true);

      // Collect all file URLs to delete
      const filesToDelete: string[] = [];
      manifest.items.forEach(item => {
        if (selectedTemplates.has(item.id)) {
          filesToDelete.push(item.previewUrl);
          filesToDelete.push(item.templateUrl);
        }
      });

      // Delete files - API now handles manifest update and cache purging
      if (filesToDelete.length > 0) {
        const result = await deleteFiles(filesToDelete, selectedPack.id, 1);
        console.log(`Deleted ${result.itemsRemoved} items, manifest updated: ${result.manifestUpdated}`);
      }

      // Force reload catalog with cache busting
      await loadCatalog(true);
      const freshCatalog = await getCatalog(true);
      const updatedPack = freshCatalog.packs.find(p => p.id === selectedPack.id);
      if (updatedPack) {
        setSelectedPack(updatedPack);
      }

      // Reload manifest with cache busting
      await loadManifest(selectedPack.id);
      setSelectedTemplates(new Set());

      alert(`✅ Successfully deleted ${selectedTemplates.size} template(s) and their files`);
    } catch (err) {
      console.error('Delete error:', err);
      alert(`Failed to delete templates: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoadingManifest(false);
    }
  };

  const handleDeleteTemplate = async (templateId: string, templateTitle: string) => {
    if (!selectedPack || !manifest) return;

    if (!confirm(`Delete template "${templateTitle}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setLoadingManifest(true);

      // Find the template to get file URLs
      const templateToDelete = manifest.items.find(item => item.id === templateId);
      if (templateToDelete) {
        // Delete files - API now handles manifest update and cache purging
        const result = await deleteFiles(
          [templateToDelete.previewUrl, templateToDelete.templateUrl],
          selectedPack.id,
          1
        );
        console.log(`Deleted ${result.itemsRemoved} items, manifest updated: ${result.manifestUpdated}`);
      }

      // Force reload catalog with cache busting
      await loadCatalog(true);
      const freshCatalog = await getCatalog(true);
      const updatedPack = freshCatalog.packs.find(p => p.id === selectedPack.id);
      if (updatedPack) {
        setSelectedPack(updatedPack);
      }

      // Reload manifest with cache busting
      await loadManifest(selectedPack.id);

      alert(`✅ Template "${templateTitle}" and its files deleted successfully`);
    } catch (err) {
      console.error('Delete error:', err);
      alert(`Failed to delete template: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoadingManifest(false);
    }
  };

  const handleSelectPackCheckbox = (packId: string) => {
    setSelectedPacks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(packId)) {
        newSet.delete(packId);
      } else {
        newSet.add(packId);
      }
      return newSet;
    });
  };


  const handleDeleteSinglePack = async (packId: string, packTitle: string) => {
    if (!confirm(`⚠️ PERMANENTLY delete bucket "${packTitle}" (${packId})? This will delete ALL files from storage and CANNOT be undone!`)) {
      return;
    }

    setLoading(true);
    try {
      await deletePack(packId);
      await loadCatalog();
      alert(`✅ Successfully deleted bucket "${packTitle}" and all its files`);
    } catch (err) {
      console.error(`Failed to delete pack ${packId}:`, err);
      alert(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedPacks.size === 0) return;

    if (!confirm(`⚠️ PERMANENTLY delete ${selectedPacks.size} selected ${selectedPacks.size === 1 ? 'category' : 'categories'}? This will delete ALL files from storage and CANNOT be undone!`)) {
      return;
    }

    setLoading(true);
    let successCount = 0;
    let errorCount = 0;

    try {
      // Delete each selected pack permanently
      for (const packId of Array.from(selectedPacks)) {
        try {
          await deletePack(packId);
          successCount++;
        } catch (err) {
          console.error(`Failed to delete pack ${packId}:`, err);
          errorCount++;
        }
      }

      // Reload catalog
      await loadCatalog();

      // Show result
      if (errorCount === 0) {
        alert(`✅ Successfully deleted ${successCount} ${successCount === 1 ? 'category' : 'categories'} and all their files`);
      } else {
        alert(`⚠️ Deleted ${successCount}, but ${errorCount} failed.`);
      }

      setSelectedPacks(new Set());
    } catch (err) {
      alert(`Failed to delete: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategoryExpansion = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const handleToggleCategoryStatus = async (category: string, packs: CatalogPack[]) => {
    try {
      // Check if all packs in category are active
      const allActive = packs.every(pack => pack.active !== false);
      const newStatus = !allActive; // Toggle to opposite

      // Update all packs in the category
      for (const pack of packs) {
        await togglePackStatus(pack.id, newStatus);
      }

      // Update local catalog
      if (catalog) {
        const updatedPacks = catalog.packs.map(pack =>
          pack.category === category ? { ...pack, active: newStatus } : pack
        );
        setCatalog({ ...catalog, packs: updatedPacks });
      }

      // Show success message
      alert(`✅ Category "${category}" ${newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err) {
      console.error('Failed to toggle category status:', err);
      alert(`Failed to update status: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  };

  // Group packs by category
  const groupedPacks = catalog?.packs.reduce((acc, pack) => {
    const category = pack.category;
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(pack);
    return acc;
  }, {} as Record<string, CatalogPack[]>) || {};

  if (loading) {
    return (
      <div className="container">
        <div className="header">
          <h2>Packs</h2>
        </div>
        <div className="card">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div className="spinner"></div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container">
        <div className="header">
          <h2>Packs</h2>
        </div>
        <div className="alert alert-error">{error}</div>
        <button className="btn btn-primary" onClick={() => loadCatalog(false)}>
          Retry
        </button>
      </div>
    );
  }

  // If a pack is selected, show template detail view
  if (selectedPack) {
    return (
      <div className="container">
        <div className="header">
          <button
            className="btn btn-secondary"
            onClick={handleBackToList}
            style={{ marginBottom: '16px' }}
          >
            <ArrowLeft size={16} />
            Back to Categories
          </button>
          <h2>{selectedPack.title}</h2>
          <p>
            <span className="badge badge-success">{selectedPack.category}</span>
            {' • '}
            {selectedPack.languages.join(', ')}
          </p>
        </div>

        {loadingManifest ? (
          <div className="card">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div className="spinner"></div>
            </div>
          </div>
        ) : manifest ? (
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <h3 style={{ margin: 0 }}>
                  Media ({manifest.items.length})
                </h3>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#666' }}>
                  <input
                    type="checkbox"
                    checked={selectedTemplates.size === manifest.items.length && manifest.items.length > 0}
                    onChange={handleSelectAllTemplates}
                    style={{ marginRight: '6px', cursor: 'pointer' }}
                  />
                  Select All
                </label>
              </div>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {selectedTemplates.size > 0 && (
                  <button
                    className="btn btn-danger"
                    onClick={handleBulkDeleteTemplates}
                    style={{ fontSize: '13px', padding: '6px 12px' }}
                  >
                    <Trash2 size={14} />
                    Delete Selected ({selectedTemplates.size})
                  </button>
                )}
                <button
                  className="btn btn-primary"
                  onClick={() => window.location.href = `/upload?category=${selectedPack.id}`}
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  <Upload size={14} />
                  Add Media
                </button>
              </div>
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '16px',
              marginTop: '20px'
            }}>
              {manifest.items.map((item) => (
                <div
                  key={item.id}
                  style={{
                    border: selectedTemplates.has(item.id) ? '2px solid #2383E2' : '1px solid #e0e0e0',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: selectedTemplates.has(item.id) ? 'rgba(35, 131, 226, 0.05)' : '#fff'
                  }}
                >
                  {/* Template Preview Image or Video */}
                  <div
                    style={{
                      width: '100%',
                      height: '200px',
                      backgroundColor: '#f5f5f5',
                      overflow: 'hidden',
                      position: 'relative'
                    }}
                  >
                    {/* Checkbox overlay */}
                    <div style={{
                      position: 'absolute',
                      top: '8px',
                      left: '8px',
                      zIndex: 10
                    }}>
                      <input
                        type="checkbox"
                        checked={selectedTemplates.has(item.id)}
                        onChange={() => handleSelectTemplate(item.id)}
                        style={{
                          cursor: 'pointer',
                          width: '18px',
                          height: '18px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </div>
                    {item.previewUrl.endsWith('.mp4') ? (
                      <>
                        <video
                          src={item.previewUrl}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover'
                          }}
                          muted
                          playsInline
                        />
                        <div style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          backgroundColor: 'rgba(0, 0, 0, 0.6)',
                          borderRadius: '50%',
                          width: '48px',
                          height: '48px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontSize: '20px'
                        }}>
                          ▶
                        </div>
                      </>
                    ) : (
                      <img
                        src={item.previewUrl}
                        alt={item.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          e.currentTarget.parentElement!.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; font-size: 48px;">🖼️</div>';
                        }}
                      />
                    )}
                  </div>

                  {/* Template Info */}
                  <div style={{ padding: '12px' }}>
                    <div style={{ fontWeight: 500, marginBottom: '4px', fontSize: '14px' }}>
                      {item.title}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {item.locale}
                    </div>
                    {item.tags && item.tags.length > 0 && (
                      <div style={{ marginTop: '8px', display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {item.tags.map((tag, idx) => (
                          <span
                            key={idx}
                            style={{
                              fontSize: '11px',
                              padding: '2px 6px',
                              backgroundColor: '#e3f2fd',
                              color: '#1976d2',
                              borderRadius: '4px'
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    <button
                      className="btn btn-secondary"
                      onClick={() => handleDeleteTemplate(item.id, item.title)}
                      style={{
                        marginTop: '12px',
                        width: '100%',
                        fontSize: '12px',
                        padding: '6px 12px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '6px'
                      }}
                    >
                      <Trash2 size={14} />
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="container">
      <div className="header">
        <h2>Categories</h2>
        <p>Manage content categories</p>
      </div>

      {catalog && (
        <div className="card">
          <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>Catalog Version:</strong> {catalog.version}
              <span style={{ marginLeft: '20px', color: '#666' }}>
                Updated: {new Date(catalog.updatedAt).toLocaleString()}
              </span>
            </div>
            <button className="btn btn-secondary" onClick={() => loadCatalog(false)}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      )}

      {catalog && catalog.packs.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">
            <Package size={48} strokeWidth={1.5} style={{ opacity: 0.4 }} />
          </div>
          <p>No categories found. Create your first category by uploading content and publishing.</p>
          <button
            className="btn btn-primary"
            onClick={() => window.location.href = '/upload'}
            style={{ marginTop: '20px' }}
          >
            <Upload size={16} />
            Upload & Create Category
          </button>
        </div>
      )}

      {catalog && catalog.packs.length > 0 && (
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h3 style={{ margin: 0 }}>Categories ({Object.keys(groupedPacks).length})</h3>
            {selectedPacks.size > 0 && (
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <span style={{ fontSize: '13px', color: '#666' }}>
                  {selectedPacks.size} selected
                </span>
                <button
                  className="btn btn-secondary"
                  onClick={handleBulkDelete}
                  style={{ fontSize: '13px', padding: '6px 12px' }}
                >
                  Delete Selected
                </button>
              </div>
            )}
          </div>
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Category</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedPacks).map(([category, packs]) => {
                const isExpanded = expandedCategories.has(category);
                return (
                  <React.Fragment key={category}>
                    {/* Category Row */}
                    <tr
                      style={{
                        backgroundColor: '#f9f9f9'
                      }}
                    >
                      <td
                        onClick={() => toggleCategoryExpansion(category)}
                        style={{ cursor: 'pointer' }}
                      >
                        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', justifyContent: 'space-between' }}>
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}
                            onClick={() => toggleCategoryExpansion(category)}
                          >
                            <span className="badge badge-success">{category}</span>
                            <span style={{ fontSize: '13px', color: '#666' }}>
                              ({packs.length} {packs.length === 1 ? 'bucket' : 'buckets'})
                            </span>
                          </div>
                          <label
                            style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: '8px' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div style={{ position: 'relative', width: '44px', height: '24px' }}>
                              <input
                                type="checkbox"
                                checked={packs.every(pack => pack.active !== false)}
                                onChange={() => handleToggleCategoryStatus(category, packs)}
                                style={{ display: 'none' }}
                              />
                              <div style={{
                                width: '44px',
                                height: '24px',
                                backgroundColor: packs.every(pack => pack.active !== false) ? '#2383E2' : '#ccc',
                                borderRadius: '12px',
                                position: 'relative',
                                transition: 'background-color 0.2s'
                              }}>
                                <div style={{
                                  width: '20px',
                                  height: '20px',
                                  backgroundColor: 'white',
                                  borderRadius: '50%',
                                  position: 'absolute',
                                  top: '2px',
                                  left: packs.every(pack => pack.active !== false) ? '22px' : '2px',
                                  transition: 'left 0.2s',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                                }} />
                              </div>
                            </div>
                            <span style={{ fontSize: '12px', color: packs.every(pack => pack.active !== false) ? '#10b981' : '#999' }}>
                              {packs.every(pack => pack.active !== false) ? 'Active' : 'Inactive'}
                            </span>
                          </label>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded Buckets */}
                    {isExpanded && (
                      <tr>
                        <td colSpan={2} style={{ padding: 0, backgroundColor: '#fafafa' }}>
                          <table style={{ width: '100%', marginBottom: 0, tableLayout: 'fixed' }}>
                            <thead>
                              <tr style={{ backgroundColor: '#f5f5f5' }}>
                                <th style={{ width: '100px', paddingLeft: '60px', textAlign: 'left' }}>
                                  <input
                                    type="checkbox"
                                    checked={packs.every(p => selectedPacks.has(p.id))}
                                    onChange={() => {
                                      const allSelected = packs.every(p => selectedPacks.has(p.id));
                                      setSelectedPacks(prev => {
                                        const newSet = new Set(prev);
                                        packs.forEach(p => {
                                          if (allSelected) {
                                            newSet.delete(p.id);
                                          } else {
                                            newSet.add(p.id);
                                          }
                                        });
                                        return newSet;
                                      });
                                    }}
                                    style={{ cursor: 'pointer' }}
                                  />
                                </th>
                                <th style={{ width: '35%', textAlign: 'left' }}>Bucket-ID</th>
                                <th style={{ textAlign: 'left' }}>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {packs.map((pack) => (
                                <tr
                                  key={pack.id}
                                  style={{
                                    backgroundColor: selectedPacks.has(pack.id) ? 'rgba(35, 131, 226, 0.05)' : '#fff'
                                  }}
                                >
                                  <td style={{ paddingLeft: '60px', width: '100px' }}>
                                    <input
                                      type="checkbox"
                                      checked={selectedPacks.has(pack.id)}
                                      onChange={() => handleSelectPackCheckbox(pack.id)}
                                      style={{ cursor: 'pointer' }}
                                    />
                                  </td>
                                  <td style={{ width: '35%' }}><code>{pack.id}</code></td>
                                  <td>
                                    <div style={{ display: 'flex', gap: '8px' }}>
                                      <button
                                        className="btn btn-primary"
                                        onClick={() => navigate(`/categories/${pack.id}`)}
                                        style={{ fontSize: '12px', padding: '6px 12px' }}
                                      >
                                        <Eye size={14} />
                                        View Media
                                      </button>
                                      <button
                                        className="btn btn-secondary"
                                        onClick={() => window.location.href = `/upload?category=${pack.id}`}
                                        style={{ fontSize: '12px', padding: '6px 12px' }}
                                      >
                                        <Upload size={14} />
                                        Add Media
                                      </button>
                                      <button
                                        className="btn"
                                        onClick={() => handleDeleteSinglePack(pack.id, pack.title)}
                                        style={{
                                          fontSize: '12px',
                                          padding: '6px 12px',
                                          background: '#eb5757',
                                          color: '#fff'
                                        }}
                                        title="Delete bucket"
                                      >
                                        <Trash2 size={14} />
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
